import { Call, Device } from '@twilio/voice-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isFirefox } from '../../utils/browser.util';

interface MeetingOptions {
    meetingid: string;
    appname: 'userapp' | 'interpreterapp';
}

function createConnectOptions({
    meetingOptions,
    audioConstraints,
}: {
    meetingOptions: MeetingOptions;
    audioConstraints: MediaTrackConstraints | undefined;
}) {
    const options: any = { params: meetingOptions };
    if (isFirefox() && audioConstraints?.deviceId) {
        options.rtcConstraints = { audio: { deviceId: audioConstraints.deviceId } };
    }
    return options;
}
export interface UseVoiceOptions {
    token: string | null;
    audioConstraints: MediaTrackConstraints | undefined;
    meetingOptions: MeetingOptions;
    muted: boolean;
}

export interface UseVoiceResult {
    device: Device | null;
    connected: boolean;
    error: string | null;
    call: Call | null;
    ringing: boolean;
}

export default function useVoice({
    token,
    audioConstraints,
    meetingOptions,
    muted,
}: UseVoiceOptions) {
    const isDeviceInitializedRef = useRef(false);
    const isDeviceReadyRef = useRef(false);
    const [connected, setConnected] = useState<boolean>(false);
    const [device, setDevice] = useState<Device | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [call, setCall] = useState<Call | null>(null);

    const handleDeviceRegistered = useCallback(
        async (_device: Device) => {
            // make sure if connection is already established
            if (!isDeviceReadyRef.current) {
                if (audioConstraints?.deviceId && !isFirefox()) {
                    try {
                        await _device.audio?.setInputDevice(audioConstraints.deviceId as string);
                    } catch {
                        console.log('Device.audio.setInputDevice failed');
                    }
                }

                const options = createConnectOptions({ meetingOptions, audioConstraints });
                const newCall = await _device.connect(options);

                setCall(newCall);
                // set default vco state
                newCall.on('accept', () => {
                    newCall.mute(muted);
                });
                isDeviceReadyRef.current = true;
            }
        },
        [isDeviceReadyRef, audioConstraints, meetingOptions, muted],
    );

    const handleConnect = useCallback(() => {
        setConnected(true);
    }, []);
    const handleDisconnect = useCallback(() => {
        setConnected(false);
    }, []);

    const handleError = useCallback((err: Error) => {
        setError(err.message);
    }, []);

    useEffect(() => {
        if (!isDeviceInitializedRef.current && token) {
            const twillioVoiceDevice = new Device(token, {
                edge: 'dublin',
                codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
            });
            setDevice(twillioVoiceDevice);
            twillioVoiceDevice.register();
            isDeviceInitializedRef.current = true;
        }
    }, []);

    useEffect(() => {
        if (device) {
            device.on('registered', () => handleDeviceRegistered(device));
            device.on('connect', handleConnect);
            device.on('disconnect', handleDisconnect);
            device.on('error', handleError);
        }
        return () => {
            device?.removeAllListeners();
        };
    }, [device, handleDeviceRegistered, handleConnect, handleDisconnect, handleError]);

    return {
        device,
        connected,
        error,
        call,
    };
}
